import type { CaseOut, CaseStatus } from "@/api";
import { stageOf } from "@/lib/stages";

import {
  FIRST_VISIT,
  GREETINGS,
  HANDOVER,
  LEADS,
  SAYINGS,
  WAITING,
  type GreetingGroup,
  type WaitingKind,
} from "./greetings";

export type GreetingCase = Pick<CaseOut, "id" | "caseNo" | "title" | "status" | "statusChangedAt">;

/** A request to me that waits for an answer — who asks, and for which Vorgang. */
export interface GreetingHandover {
  fromDisplayName: string;
  title: string;
}

export interface GreetingContext {
  hour: number;
  /** 0 = Sunday … 6 = Saturday, as `Date.getDay()` counts. */
  weekday: number;
  firstName: string;
  isFirstVisit: boolean;
  /**
   * The caller's own cases, or `null` when the list could not be fetched.
   * `null` is not „nothing waits": a greeting then says nothing about the
   * load at all, rather than something false.
   */
  cases: readonly GreetingCase[] | null;
  /**
   * Requests to me that wait (UX gate round 1, #1) — named before anything
   * else, a first visit included. `null` or empty: none, or not known.
   */
  handovers?: readonly GreetingHandover[] | null;
  /**
   * Ids of my Vorgänge whose latest calculation names a blocker
   * (`VolumeOut.blockerCodes`) — a release is refused there, so they do not
   * „wait for my release" (15.10.2026). `null` or absent: not known; then the
   * status alone counts, as before.
   */
  blocked?: ReadonlySet<string> | null;
}

/** The one case the greeting offers to continue with. */
export interface GreetingFocus {
  caseNo: string;
  title: string;
  path: string;
}

export interface Greeting {
  salutation: string;
  line: string;
  /** A piece of `line` to set in the figure face — the case number — or nothing. */
  figure: string | null;
  focus: GreetingFocus | null;
}

function salutationFor(hour: number, isFirstVisit: boolean, name: string): string {
  if (isFirstVisit) return `Willkommen, ${name}.`;
  if (hour < 5 || hour >= 22) return `Spät geworden, ${name}.`;
  if (hour < 7) return `Früh dran, ${name}.`;
  if (hour < 11) return `Guten Morgen, ${name}.`;
  if (hour < 18) return `Guten Tag, ${name}.`;
  return `Guten Abend, ${name}.`;
}

const isMondayMorning = (ctx: GreetingContext) => ctx.weekday === 1 && ctx.hour >= 5 && ctx.hour < 12;
const isFridayAfternoon = (ctx: GreetingContext) => ctx.weekday === 5 && ctx.hour >= 14;
const isWeekend = (ctx: GreetingContext) => ctx.weekday === 0 || ctx.weekday === 6;

/** The pool for a day with nothing concrete to say: weekday first, then the hour. */
function idleGroup(ctx: GreetingContext): GreetingGroup {
  if (isWeekend(ctx)) return "weekend";
  if (isFridayAfternoon(ctx)) return "friday";
  if (isMondayMorning(ctx)) return "monday";
  if (ctx.hour >= 5 && ctx.hour < 7) return "early";
  if (ctx.hour >= 7 && ctx.hour < 11) return "morning";
  if (ctx.hour >= 11 && ctx.hour < 14) return "midday";
  if (ctx.hour >= 14 && ctx.hour < 19) return "afternoon";
  return "late";
}

function leadFor(ctx: GreetingContext): string | null {
  if (isWeekend(ctx)) return LEADS.weekend;
  if (isFridayAfternoon(ctx)) return LEADS.friday;
  if (isMondayMorning(ctx)) return LEADS.monday;
  return null;
}

const KIND_OF: Partial<Record<CaseStatus, WaitingKind>> = {
  quote_approved: "approved",
  calculation_ready: "release",
  commercial_review: "release",
  price_data_missing: "release",
  planning_ready: "planning",
  planning_in_progress: "planning",
  technical_review: "planning",
  documents_captured: "documents",
  extraction_review: "documents",
  question_open: "documents",
  extraction_failed: "documents",
  new: "new",
  sent: "sent",
};

const KINDS = Object.keys(WAITING) as WaitingKind[];

/** The stages the greeting counts as „waiting for my release" — what `StartScreen` asks the blockers of. */
export const RELEASE_STATUSES = (Object.keys(KIND_OF) as CaseStatus[]).filter((status) => KIND_OF[status] === "release");

const NUMBER_WORDS = [
  "null", "ein", "zwei", "drei", "vier", "fünf", "sechs",
  "sieben", "acht", "neun", "zehn", "elf", "zwölf",
];

/** A count as a word up to twelve, then digits — capitalised for a sentence start. */
export function countWord(n: number): string {
  const word = n <= 12 ? NUMBER_WORDS[n]! : String(n);
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function waitingLine(cases: readonly GreetingCase[], blocked: ReadonlySet<string> | null) {
  for (const kind of KINDS) {
    const matching = cases
      .filter((c) => KIND_OF[c.status] === kind && !(kind === "release" && blocked?.has(c.id)))
      .sort((a, b) => b.statusChangedAt.localeCompare(a.statusChangedAt));
    const newest = matching[0];
    if (!newest) continue;
    const texts = WAITING[kind];
    const line =
      matching.length === 1
        ? texts.one.replace("{caseNo}", newest.caseNo)
        : texts.many.replace("{n}", countWord(matching.length));
    const entry = stageOf(newest.status).entry === "calculation" ? "kalkulation" : "planung";
    return {
      line,
      figure: matching.length === 1 ? newest.caseNo : null,
      focus: { caseNo: newest.caseNo, title: newest.title, path: `/vorgang/${newest.id}/${entry}` },
    };
  }
  return null;
}

/** „Denis möchte dir „Terrasse“ übergeben." — the sender by first name, like the salutation. */
function handoverLine(handovers: readonly GreetingHandover[]): string | null {
  const [first] = handovers;
  if (first === undefined) return null;
  if (handovers.length > 1) return HANDOVER.many.replace("{n}", countWord(handovers.length));
  const from = first.fromDisplayName.trim().split(/\s+/)[0] || first.fromDisplayName;
  return HANDOVER.one.replace("{from}", from).replace("{title}", first.title);
}

/**
 * One greeting for this visit. `random` is injected (0 ≤ r < 1) so a test can
 * pin the pick among the idle sentences; the screen passes a frozen draw.
 *
 * Order: a request to me is named first — on a first visit too, where it is
 * the one thing to do; then a first visit is welcomed and told how to begin;
 * otherwise the most pressing thing that waits is named, with the day of the
 * week as a lead; and only when nothing waits — or the list could not be
 * read — a sentence for the day and the hour. A request offers no „Weiter
 * mit …": its answer is the card right under the greeting.
 */
export function pickGreeting(ctx: GreetingContext, random: () => number): Greeting {
  const salutation = salutationFor(ctx.hour, ctx.isFirstVisit, ctx.firstName);
  const lead = ctx.isFirstVisit ? null : leadFor(ctx);

  const asked = handoverLine(ctx.handovers ?? []);
  if (asked !== null) return { salutation, line: lead ? `${lead} ${asked}` : asked, figure: null, focus: null };

  if (ctx.isFirstVisit) return { salutation, line: FIRST_VISIT, figure: null, focus: null };

  const waiting = ctx.cases === null ? null : waitingLine(ctx.cases, ctx.blocked ?? null);
  if (waiting) return { salutation, ...waiting, line: lead ? `${lead} ${waiting.line}` : waiting.line };

  const texts = GREETINGS[idleGroup(ctx)];
  const index = Math.min(texts.length - 1, Math.floor(random() * texts.length));
  return { salutation, line: texts[index]!, figure: null, focus: null };
}

/** Whole days since 1970-01-01 for the *local* calendar date. */
function dayNumber(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000);
}

/**
 * A step coprime to the list length walks through every saying before one
 * comes back, and never shows the same one two days running. Changing the
 * list length keeps that true as long as the step stays coprime to it — the
 * test checks.
 */
export const SAYING_STEP = 17;

/** Spruch des Tages: the same sentence all day and on every reload, a different one tomorrow. */
export function sayingFor(date: Date): string {
  return SAYINGS[(dayNumber(date) * SAYING_STEP) % SAYINGS.length]!;
}
